import Vue from 'vue';
import { HTTP } from '@/utils/httpCommonUtil';
import { BallPulseLoader } from 'vue-loaders';
import VueSimpleSpinner from 'vue-simple-spinner';
import { getRestParamErrorMessage, getRestResponseErrorMessage } from '@/utils/serverErrorMessageUtil';
import flatPickr from 'vue-flatpickr-component';
import format from 'date-fns/format'
import {adminRoles, UserRoleEnum} from '@/enums/userRolesEnum';
import GenericModal from '@/componentsReusable/genericModal.vue';
import TimePickerControl from '@/componentsReusable/TimePickerControl.vue'
import { convertDateStringToUTCDate } from '@/utils/dateUtil';
import ShiftEventHub from '@/components/shift/ShiftEventHub';

export default Vue.component('shift-info-card', {
  name: 'ShiftInfoCard',
  data () {
    let emptyArray: Array<any> = [];
    return {
      cardExpanded: false,
      loadingInitialData: false,
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      isAuthorized: null,
      shiftInfos: emptyArray,
      showConfirmationModal: false,
      frozenUntil: null,
      endDateValid: true
    }
  },
  components: {
    BallPulseLoader,
    VueSimpleSpinner,
    GenericModal,
    TimePickerControl,
    flatPickr
  },
  computed: {
    isProviderUser (): boolean {
      return this.$store.getters['user/hasOnlyRole'](UserRoleEnum.PROVIDER)
    },
    isFacilityUser (): boolean {
      return this.$store.getters['user/hasOnlyRole'](UserRoleEnum.FACILITY_CONTACT)
    }
  },
  beforeMount: function () {
    this.isAuthorized = this.$store.getters['user/hasRole'](adminRoles);
  },
  created: function () {
    this.expandCard();
  },
  filters: {
    hourFilter (totalTime: string) {
      if (totalTime && totalTime.length) {
        let iColon = totalTime.indexOf(":");
        return totalTime.substring(0,iColon);
      }
      return '';
    },
    minuteFilter (totalTime: string) {
      if (totalTime && totalTime.length) {
        let iColon = totalTime.indexOf(":");
        return totalTime.substring(iColon + 1, iColon + 3);
      }
      return '';
    },
    dateformat (dateISO: string) {
      return format(convertDateStringToUTCDate(dateISO) as any, 'MMM DD, YYYY - hh:mm A');
    },
    shiftDuration: function (shiftInfo: any) {
      let hour = 0;
      let minutes = 0;
      if (shiftInfo.shiftDuration) {
        // Turn the ISO startdate string into a date object, but be sure to cut off any 'Z' that would signify UTC time
        hour = Math.floor(shiftInfo.shiftDuration / 60);
        minutes = Math.round(shiftInfo.shiftDuration % 60);
      }
      let hoursDisplay = hour + " Hrs";
      let minutesDisplay = minutes ? ", " + minutes + " Min" : "";
      
      return hoursDisplay + minutesDisplay;
    }
  },
  methods: {
    expandCard () {
      this.cardExpanded = !this.cardExpanded;
      if (this.cardExpanded) {
        this.getShiftInfo()
      }
    },
    getShiftInfo () {      
      let self = this;
      let showLoadingSpinnerTimeout = setTimeout(function () {
        self.loadingInitialData = true;
      }, 0);
      HTTP.get('/api/shifts/' + this.$route.params.shiftId + '/shift-datetimes')
      .then(function (response) {
        clearTimeout(showLoadingSpinnerTimeout);
        self.loadingInitialData = false;
        if (!response.data || !response.data.data) {
          throw new Error("Error retrieving Shift Information.");
        }
        let freshShiftInfos = response.data.data;
        
        freshShiftInfos.forEach( function (shiftInfo: any) {
          shiftInfo.isViewing = true;
          shiftInfo.isEditing = false;
          shiftInfo.updatingData = false;
          self.checkFrozen(shiftInfo);
          self.startDate = shiftInfo.startDatetime.substr(0,10);
          self.endDate = shiftInfo.endDatetime.substr(0,10);
          self.startTime = shiftInfo.startDatetime;
          self.endTime = shiftInfo.endDatetime;
        });
        self.shiftInfos = freshShiftInfos;
      })
      .catch(function (err) {
        console.log('response Error! ' + err);
        clearTimeout(showLoadingSpinnerTimeout);
        self.loadingInitialData = false;
        self.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred retrieving Shift Information.'
        });
      })
    },
    checkFrozen(shiftInfo: any) {
      if (!shiftInfo.invoices || shiftInfo.invoices.length === 0) {
        return;
      }
      let frozenUntil;
      for (const invoice of shiftInfo.invoices) {
        const date = new Date(invoice.invoiceEndDate);
        date.setDate(date.getDate() + 1);
        if (!frozenUntil || date.getTime() > (frozenUntil as Date).getTime()) {
          frozenUntil = date;
        }
      }
      shiftInfo.mayEditFrozen = Boolean(frozenUntil && new Date(shiftInfo.endDatetime).getTime() > frozenUntil.getTime());
      if (shiftInfo.mayEditFrozen) {
        (this.frozenUntil as any) = frozenUntil;
      }
    },
    toggleEditShiftInfo (shiftInfo: any) {
      if (this.isProviderUser || this.isFacilityUser) {
        return;
      }
      shiftInfo.isEditing = !shiftInfo.isEditing;
    },
    cancel (shiftInfo: any) {
      shiftInfo.isEditing = false;
      this.getShiftInfo();
    },
    startDateChange (shiftInfo: any, selectedDate: any) {
      this.startDate = selectedDate[0].toISOString().substr(0,10);
      shiftInfo.startDatetime = selectedDate[0].toISOString().substr(0,10) + shiftInfo.startDatetime.substr(10);
    },
    endDateChange (shiftInfo: any, selectedDate: Date[]) {
      const end = selectedDate[0].toISOString().substring(0, 10);
      if (this.frozenUntil && (this.frozenUntil as any).getTime() > new Date(end).getTime()) {
        shiftInfo.endDatetimeErrorMessage = ' End Date must be after ' + format((this.frozenUntil as any).toISOString(), 'MM.DD.YYYY');
        this.endDate = shiftInfo.endDatetime.substr(0, 10);
        this.endDateValid = false;
        return;
      } else {
        shiftInfo.endDatetimeErrorMessage = '';
        this.endDateValid = true;
      }
      this.endDate = end;
      shiftInfo.endDatetime = selectedDate[0].toISOString().substr(0,10) + shiftInfo.endDatetime.substr(10);
    },
    startTimeSelected (shiftInfo: any, time: any) {
      this.startTime = shiftInfo.startDatetime.substr(0,10) + "T" + time + ":00.000Z";
      shiftInfo.startDatetime = shiftInfo.startDatetime.substr(0,10) + "T" + time + ":00.000Z";
    },
    endTimeSelected (shiftInfo: any, time: any) {
      this.endTime = shiftInfo.endDatetime.substr(0,10) + "T" + time + ":00.000Z";
      shiftInfo.endDatetime = shiftInfo.endDatetime.substr(0,10) + "T" + time + ":00.000Z";
    },
    updateShiftInfo (updatedShiftInfo: any) {
      let self = this
      updatedShiftInfo.updatingData = true;

      HTTP.put('/api/shifts/' + this.$route.params.shiftId + '/shift-datetimes', updatedShiftInfo)
      .then(function (response) {
        updatedShiftInfo.updatingData = false;
        updatedShiftInfo.isEditing = false;

        // Broadcast, to whoever is listening, that a new shift's datetime has changed.
        ShiftEventHub.$emit('SHIFT_DATETIME_CHANGE');

        self.getShiftInfo();
      })
      .catch(function (error) {
        updatedShiftInfo.updatingData = false;

        updatedShiftInfo.startDatetimeErrorMessage = getRestParamErrorMessage(error, 'startDatetime');
        updatedShiftInfo.endDatetimeErrorMessage = getRestParamErrorMessage(error, 'endDatetime');

        if (!updatedShiftInfo.startDatetimeErrorMessage.length &&
          !updatedShiftInfo.endDatetimeErrorMessage.length ) {
          let generalError = getRestResponseErrorMessage(error);
          self.$notify({
            group: 'main',
            type: 'error',
            title: 'Error Updating',
            text: generalError ? generalError : 'An error occurred updating this shift.',
          });
        }
      });
    },
    browseToInvoice (invoiceId: number) {
      window.open("/invoices/" + invoiceId, '_blank');
    }
  }
})



// WEBPACK FOOTER //
// ./src/components/shift/cards/shiftInfo/ShiftInfoCard.ts